@layer refine.base {
  :host {
    --_radius: var(--command-palette-radius, var(--rounded-lg));
    --_max-width: var(--command-palette-max-width, 40rem);
    --_backdrop-bg: var(--command-palette-backdrop, color-mix(in oklch, var(--color-canvas) 40%, transparent));
    --_panel-bg: var(--command-palette-bg, var(--color-canvas));
    --_panel-border-color: var(--command-palette-border-color, var(--color-divider));
    --_option-hover-bg: var(--command-palette-option-hover-bg, var(--color-contrast-100));
    --_option-focus-bg: var(
      --command-palette-option-focus-bg,
      color-mix(in oklch, var(--color-primary) 12%, var(--color-contrast-100))
    );

    display: contents;
  }

  /* =====================
       Native <dialog> resets — see ore-dialog for the full rationale.
       ===================== */

  dialog {
    all: unset;
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    padding-block-start: min(14vh, var(--size-24));
    margin: 0;
    overflow: hidden;
    pointer-events: none;
    background: transparent;
    border: none;
    transition:
      display 200ms allow-discrete,
      overlay 200ms allow-discrete;
  }

  dialog:not([open]) {
    display: none;
  }

  dialog:not([open]) .panel {
    display: none;
  }

  dialog[open] {
    pointer-events: auto;
  }

  dialog::backdrop {
    background: var(--_backdrop-bg);
    opacity: 1;
    backdrop-filter: blur(var(--blur-xs));
    transition:
      opacity var(--transition-normal),
      overlay var(--transition-normal) allow-discrete,
      display var(--transition-normal) allow-discrete;

    @starting-style {
      opacity: 0;
    }
  }

  dialog.closing::backdrop {
    opacity: 0;
  }

  /* =====================
       Panel
       ===================== */

  .panel {
    position: relative;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    width: var(--_max-width);
    max-width: calc(100vw - var(--size-8));
    max-height: min(28rem, calc(100dvh - var(--size-16)));
    overflow: hidden;
    background: var(--_panel-bg);
    border: var(--border) solid var(--_panel-border-color);
    border-radius: var(--_radius);
    box-shadow: var(--command-palette-shadow, var(--shadow-2xl));
    opacity: 1;
    backdrop-filter: blur(var(--blur-md));
    transform: scale(1) translateY(0);
    transition:
      opacity 200ms var(--ease-spring),
      transform 200ms var(--ease-spring);

    @starting-style {
      opacity: 0;
      transform: scale(var(--overlay-enter-scale-dialog)) translateY(-4px);
    }
  }

  dialog.closing .panel {
    opacity: 0;
    transform: scale(var(--overlay-enter-scale-dialog)) translateY(-4px);
    transition:
      opacity 150ms var(--ease-spring),
      transform 150ms var(--ease-spring);
  }

  @media (width <= 640px) {
    .panel {
      width: calc(100vw - var(--size-4));
      max-width: calc(100vw - var(--size-4));
    }

    dialog {
      padding-block-start: var(--size-4);
    }
  }

  /* =====================
       Search field
       ===================== */

  .search {
    display: flex;
    flex-shrink: 0;
    gap: var(--size-2);
    align-items: center;
    padding: var(--size-3) var(--size-4);
    border-block-end: var(--border) solid var(--color-contrast-100);
  }

  .search-icon {
    display: inline-flex;
    flex-shrink: 0;
    color: var(--color-contrast-400);
  }

  .search-input {
    flex: 1;
    min-width: 0;
    padding: 0;
    font: inherit;
    font-size: var(--text-md);
    color: var(--color-contrast-900);
    outline: none;
    background: transparent;
    border: none;
  }

  .search-input::placeholder {
    color: var(--color-contrast-400);
  }

  .search-loader {
    display: inline-flex;
    flex-shrink: 0;
    width: 1em;
    height: 1em;
    border: 2px solid var(--color-contrast-300);
    border-inline-end-color: transparent;
    border-radius: 50%;
    animation: command-palette-spin var(--transition-loader) infinite;
  }

  .search-loader[hidden] {
    display: none;
  }

  @keyframes command-palette-spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* =====================
       Listbox
       ===================== */

  .listbox {
    flex: 1;
    padding: var(--size-1);
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: thin;
  }

  .empty {
    padding: var(--size-6) var(--size-3);
    font-size: var(--text-sm);
    color: var(--color-contrast-400);
    text-align: center;
  }

  .group-heading {
    padding: var(--size-2) var(--size-2-5) var(--size-1);
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-contrast-500);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wide, 0.05em);
  }

  .item {
    display: flex;
    gap: var(--size-2-5);
    align-items: center;
    padding: var(--size-2) var(--size-2-5);
    font-size: var(--text-sm);
    line-height: var(--leading-normal);
    color: var(--color-contrast-800);
    cursor: pointer;
    border-radius: var(--rounded-md);
    transition: background var(--transition-fast);
  }

  .item:hover:not([data-disabled]) {
    background: var(--_option-hover-bg);
  }

  .item[data-focused]:not([data-disabled]) {
    background: var(--_option-focus-bg);
  }

  .item[data-disabled] {
    color: var(--color-contrast-400);
    cursor: not-allowed;
    opacity: 0.6;
  }

  .item-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    color: var(--color-contrast-500);
  }

  .item-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .item-shortcut {
    display: inline-flex;
    flex-shrink: 0;
    gap: var(--size-1);
  }

  .item-shortcut kbd {
    padding: var(--size-0-5) var(--size-1-5);
    font-family: var(--font-mono, monospace);
    font-size: var(--text-xs);
    color: var(--color-contrast-500);
    background: var(--color-contrast-100);
    border: var(--border) solid var(--color-contrast-200);
    border-bottom-width: calc(var(--border) + 1px);
    border-radius: var(--rounded-sm);
  }

  /* =====================
       Footer hints
       ===================== */

  .footer {
    display: flex;
    flex-shrink: 0;
    gap: var(--size-4);
    align-items: center;
    padding: var(--size-2) var(--size-4);
    font-size: var(--text-xs);
    color: var(--color-contrast-500);
    border-block-start: var(--border) solid var(--color-contrast-100);
  }

  .footer-hint {
    display: inline-flex;
    gap: var(--size-1);
    align-items: center;
  }

  .footer kbd {
    padding: var(--size-0-5) var(--size-1);
    font-family: inherit;
    font-size: var(--text-xs);
    background: var(--color-contrast-100);
    border: var(--border) solid var(--color-contrast-200);
    border-bottom-width: calc(var(--border) + 1px);
    border-radius: var(--rounded-sm);
  }

  /* Slotted item elements are pure data nodes — never rendered directly. */
  ::slotted(ore-command-palette-item) {
    display: none;
  }
}
